"use client";

import React from "react";
import AspectRatio from "@/components/layout/AspectRatio";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import Image from "../../image";
import SkeletonImage from "../../parts/SkeletonImage";
import type { ImageWithSkeletonProps } from "./types";

const aspectRatioMap = {
  square: "1:1",
  portrait: "3:4",
  landscape: "4:3",
  wide: "16:9",
  ultrawide: "21:9",
  tall: "2:3",
  long: "2:1",
  "very-long": "4:1",
};

function ImageWithSkeleton({
  src,
  alt,
  width,
  height,
  fill = false,
  sizes = "100vw",
  presetRatio,
  className,
  aspectRatioClassName,
  skeletonClassName,
  ...props
}: ImageWithSkeletonProps) {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const handleOnLoad = React.useCallback(() => {
    setIsLoaded(prev => !prev);
  }, []);

  const handleOnError = React.useCallback(() => {
    setIsLoaded(prev => !prev);
  }, []);

  return (
    <AspectRatio
      presetRatio={presetRatio}
      className={cn("w-full h-full", aspectRatioClassName)}
    >
      <SkeletonImage isLoaded={isLoaded} className={skeletonClassName}>
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          fill={fill}
          sizes={sizes}
          onLoad={handleOnLoad}
          onError={handleOnError}
          aspectRatio={aspectRatioMap[presetRatio]}
          className={cn(className, classes)}
          {...native}
        />
      </SkeletonImage>
    </AspectRatio>
  );
}

export default ImageWithSkeleton;
